import * as E from "fp-ts/es6/Either";

import { SwarovskiOptikSegmentShort } from "@swo/shared";

import type {
  CheckoutShippingChange,
  ClientCartActionCartRef,
  ClientCartFullBillingAddress,
  SwarovskiOptikClientCart,
  SwarovskiOptikLocaleFrontend,
  InputQueryDto,
  RecreateMyCartInfoEffectResponse,
  RecreateMyCartInfoEffectResponseError,
  RecreateMyCartInfoEffectResponseSuccess,
  InputBodyDto as RecreateMyCartEffectInputBodyDto,
  RecreateMyCartEffectResponseError,
  RecreateMyCartEffectResponseSuccess,
  RecreateMyCartEffectResponse,
  UnknownErrorT,
} from "@swo/storefront";

import {
  CartClientActionResponse,
  ClientCartCheckoutShippingChangeAction,
  MyCartAction_AddToCart,
  MyCartAction_ChangeLineItemAmount,
  MyCartAction_RemoveFromCart,
  MyCartAction_SelectedShipping,
} from "../types";

import {
  addDiscountCodeAction,
  addToCart,
  changeLineItemAmount,
  checkoutShipping,
  removeDiscountCodeAction,
  removeFromCart,
  setBillingAddress,
} from "./internals/actions";
import { customerDataChangeAction } from "./internals/actions/code-action";
import { fetchApiJson, getEndpointForFetch } from "../../data-layer/fetch-api";

export type CustomerDataChangePayload = Partial<{
  email: string;
  firstName: string;
  lastName: string;
  title: "male" | "female" | "undefined";
  companyName: string;
  vatNumber: string;
}> & {
  buyAsCompany: boolean;
};

const unknownError: UnknownErrorT = {
  tag: "unknown-error",
};

export class CartMutationClient {
  constructor(
    private readonly locale: SwarovskiOptikLocaleFrontend,
    private readonly segment: SwarovskiOptikSegmentShort,
    private readonly token: string,
    private readonly baseUrl?: URL,
  ) {}

  private authorizedGet(): RequestInit {
    return {
      method: "GET",
      headers: {
        authorization: `Bearer ${this.token}`,
        "Content-Type": "application/json",
      },
    };
  }

  private authorizedPost(body: string): RequestInit {
    return {
      method: "POST",
      headers: {
        authorization: `Bearer ${this.token}`,
        "Content-Type": "application/json",
      },
      body,
    };
  }

  private cartEndpoint(path: `/${string}`) {
    const localeCode = this.locale.code;
    const segmentKey = this.segment.key;

    return getEndpointForFetch(`/storefront/shop/me/${localeCode}/${segmentKey}/cart${path}`, this.baseUrl);
  }

  addToCart(cartRef: ClientCartActionCartRef | null, data: MyCartAction_AddToCart["items"]): CartClientActionResponse {
    return addToCart(this.token, this.locale.code, this.segment.key, cartRef, data, this.baseUrl);
  }

  removeFromCart(
    cartRef: ClientCartActionCartRef,
    data: MyCartAction_RemoveFromCart["lineItemId"],
  ): CartClientActionResponse {
    return removeFromCart(this.token, this.locale.code, this.segment.key, cartRef, data);
  }

  changeLineItemAmount(
    cart: SwarovskiOptikClientCart,
    data: Pick<MyCartAction_ChangeLineItemAmount, "lineItemId" | "change">,
  ): CartClientActionResponse {
    return changeLineItemAmount(this.token, this.locale.code, this.segment.key, cart, data);
  }

  setBillingAddress(
    cartRef: ClientCartActionCartRef,
    billingAddress: ClientCartFullBillingAddress,
    preselection: MyCartAction_SelectedShipping | undefined,
  ): CartClientActionResponse {
    const preselectionAction: CheckoutShippingChange["method"] | undefined = preselection
      ? preselection.kind === "parcel-shipping"
        ? {
            kind: "parcel-shipping",
            id: preselection.id,
            shippingAddress: preselection.shippingAddress,
          }
        : preselection.kind === "digital"
          ? { kind: "digital", id: preselection.id }
          : {
              kind: "dealer-pickup",
              id: preselection.id,
              dealerId: preselection.dealerId,
            }
      : undefined;

    return setBillingAddress(
      this.token,
      this.locale.code,
      this.segment.key,
      cartRef,
      billingAddress,
      preselectionAction,
      this.baseUrl,
    );
  }

  setCheckoutShippingState(
    cartRef: ClientCartActionCartRef,
    payload: ClientCartCheckoutShippingChangeAction,
  ): CartClientActionResponse {
    return checkoutShipping(this.token, this.locale.code, this.segment.key, cartRef, payload, this.baseUrl);
  }

  addDiscountCodeAction(cartRef: ClientCartActionCartRef, discountCode: string): CartClientActionResponse {
    return addDiscountCodeAction(this.token, this.locale.code, this.segment.key, cartRef, discountCode, this.baseUrl);
  }

  removeDiscountCodeAction(cartRef: ClientCartActionCartRef, discountCode: string): CartClientActionResponse {
    return removeDiscountCodeAction(
      this.token,
      this.locale.code,
      this.segment.key,
      cartRef,
      discountCode,
      this.baseUrl,
    );
  }

  customerDataChange(cartRef: ClientCartActionCartRef, data: CustomerDataChangePayload): CartClientActionResponse {
    return customerDataChangeAction(this.token, this.locale.code, this.segment.key, cartRef, data, this.baseUrl);
  }

  changeCountryPlan(
    cartRef: ClientCartActionCartRef,
    data: InputQueryDto,
  ): Promise<E.Either<RecreateMyCartInfoEffectResponseError, RecreateMyCartInfoEffectResponseSuccess>> {
    const query = new URLSearchParams(data);
    const absoluteUrl = this.cartEndpoint("/recreate") + "?" + query.toString();

    return fetchApiJson<RecreateMyCartInfoEffectResponse>(absoluteUrl, this.authorizedGet())
      .then(res => {
        if ("errors" in res) {
          return E.left(res);
        } else {
          return E.right(res);
        }
      })
      .catch(res => wrapErrors<RecreateMyCartInfoEffectResponseError>(res));
  }

  moveCartTo(
    cartRef: ClientCartActionCartRef,
    data: { countryIsoCode: string; languageIsoCode: string },
  ): Promise<E.Either<RecreateMyCartEffectResponseError, RecreateMyCartEffectResponseSuccess>> {
    const absoluteUrl = this.cartEndpoint("/recreate");

    const actionBody: RecreateMyCartEffectInputBodyDto = {
      cartRef,
      targetCountryIsoCode: data.countryIsoCode,
      languageIsoCodePreference: data.languageIsoCode,
    };

    return fetchApiJson<RecreateMyCartEffectResponse>(absoluteUrl, this.authorizedPost(JSON.stringify(actionBody)))
      .then(res => {
        if ("errors" in res) {
          return E.left(res);
        } else {
          return E.right(res);
        }
      })
      .catch(res => wrapErrors<RecreateMyCartEffectResponseError>(res));
  }
}

function wrapErrors<Error>(res: any): E.Either<Error | { errors: UnknownErrorT }, any> {
  if ("errors" in res) {
    return E.left(res);
  } else {
    return E.left({ errors: unknownError });
  }
}
